import { WidgetProps } from '../../models/widget.model';
import DataGrid from '../datagrid/DataGrid';
import { GridDataStateChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { DATE_FORMAT } from '../../constants';
import { useCallback, useEffect, useState } from 'react';
import { process, State } from '@progress/kendo-data-query';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectSearchQuery } from '../../store/slices/search.slice';
import { TagRepositoryRegisterPayload } from '../../models/tag.model';
import {
  clearSelection,
  deselect,
  multiSelect,
  select,
  selectSelection,
} from '../../store/slices/selection.slice';
import { addTagRepository } from '../../store/slices/tag-editor.slice';
import { ListResponse } from '../../store/api/chart.api';
import { formatAppDate, searchInObject } from '../../hooks/utils';
import { ColumnModel } from 'common/models/widget-config.model';

export const columns: ColumnModel[] = [
  {
    title: 'Date',
    field: 'date',
    filter: 'date',
    sortable: true,
    cell: ({ dataItem, field }: any) => {
      return <td>{formatAppDate(dataItem[field as string], DATE_FORMAT)}</td>;
    },
  },
  {
    sortable: true,
    title: 'HCP',
    field: 'hcp',
  },
  {
    sortable: true,
    title: 'Medical Inquiry Name',
    field: 'medicalInquiryName',
    filter: 'text',
  },
  {
    sortable: true,
    title: 'Channel',
    field: 'attributes.Channel',
    cell: ({ dataItem }) => {
      return <td>{dataItem['attributes']['Channel']}</td>;
    },
  },
  {
    title: 'MIR Type',
    field: 'attributes.MIRType',
    cell: ({ dataItem }) => {
      return <td>{dataItem['attributes']['MIRType']}</td>;
    },
  },
  {
    title: 'Source',
    field: 'attributes.Source',
    cell: ({ dataItem }) => {
      return <td>{dataItem['attributes']['Source']}</td>;
    },
  },
  {
    title: 'Delivery Method',
    field: 'deliveryMethod',
  },
  {
    title: 'Product',
    field: 'product',
  },
  {
    title: 'Closed Date',
    field: 'closedDate',
    sortable: true,
    filter: 'date',
    cell: ({ dataItem, field }: any) => {
      return <td>{formatAppDate(dataItem[field as string], DATE_FORMAT)}</td>;
    },
  },
];

const SELECTION_TYPE = 'mir';

export default function WidgetResponseDetails(props: Readonly<WidgetProps<ListResponse>>) {
  const dispatch = useAppDispatch();
  const selection = useAppSelector(selectSelection);
  const filterValue = useAppSelector(selectSearchQuery);
  const [filteredData, setFilteredData] = useState(props.data.data);
  const [dataState, setDataState] = useState<State>(props.data as State);
  const [dataResult, setDataResult] = useState(process(filteredData, dataState));

  const onFilterChange = useCallback(
    (value: string) => {
      const newData =
        value.length > 0
          ? props.data.data.filter((item: Record<string, unknown>) =>
              searchInObject(item, value, [
                'date',
                'profile',
                'responseDetail',
                'response',
                'profile',
                'persona',
                'fieldTeam',
                'specialtyName',
                'tags',
              ])
            )
          : props.data.data;
      setFilteredData(newData);
      setDataState((state) => {
        const clearedPagerDataState = { ...state };
        const processedData = process(newData, clearedPagerDataState);

        setDataResult(processedData);

        return clearedPagerDataState;
      });
    },
    [props.data.data]
  );

  useEffect(() => {
    onFilterChange(filterValue);
  }, [filterValue, dispatch, onFilterChange]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const data = filteredData?.map((item: Record<string, any>) => ({
      ...(item ?? {}),
      tagsCounter: Object.keys(item?.tags ?? {}).length,
    }));
    setDataResult(process(data, event.dataState));
    setDataState(event.dataState);
  };

  const onSelect = useCallback(
    (ev: Record<string, unknown>) => {
      if (ev.mode == 'multiple') {
        const target = (ev.dataItem as Record<string, unknown>).responseId;
        const payload = {
          type: SELECTION_TYPE,
          value: target,
        };
        const selectedAlready = selection[SELECTION_TYPE]
          ? selection[SELECTION_TYPE].includes(target)
          : false;
        if (selectedAlready) {
          dispatch(deselect(payload));
        } else {
          dispatch(select(payload));
        }
      } else {
        const currentSelection = selection[SELECTION_TYPE];
        if (currentSelection && currentSelection.length === (ev.dataItems as unknown[]).length) {
          // Deselect all is it then guess work due to how kendo sends select all event....
          dispatch(clearSelection({ type: SELECTION_TYPE }));
        } else {
          dispatch(
            multiSelect({
              type: SELECTION_TYPE,
              value: (ev.dataItems as Record<string, unknown>[]).map((item) => item.responseId),
            })
          );
        }
      }
    },
    [selection, dispatch]
  );

  useEffect(() => {
    setFilteredData(props.data.data);
    setDataState(props.data as State);
  }, [props.data]);

  useEffect(() => {
    const data = filteredData?.map((item: any) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      ({ ...(item ?? {}), tagsCounter: Object.keys(item?.tags ?? {}).length })
    );
    setDataResult(process(data, dataState));
  }, [filteredData, dataState]);

  useEffect(() => {
    const payload: TagRepositoryRegisterPayload = {
      repository: SELECTION_TYPE,
      records: {},
    };
    dataResult.data.forEach((row: { responseId: number; tags: any[] }) => {
      payload.records[row.responseId] = row.tags;
    });
    dispatch(addTagRepository(payload));
  }, [dispatch, dataResult]);

  const dataResultWithSelection = {
    ...dataResult,
    data: dataResult.data.map((item: { [key: string]: any }) => {
      return {
        ...item,
        selected: selection[SELECTION_TYPE]
          ? selection[SELECTION_TYPE].includes(item.responseId as number)
          : false,
        tagsCounter: Object.keys(item.tags ?? {}).length,
      } as unknown;
    }),
  };

  return (
    <DataGrid
      widgetId={props.id}
      isSelectable={false}
      onDataStateChange={dataStateChange}
      {...dataState}
      groupable={props.groupable}
      total={props.data.total}
      sortable={true}
      data={dataResultWithSelection}
      columns={columns}
      resizable={true}
      onSelectionChange={(event: GridSelectionChangeEvent) =>
        onSelect(event as unknown as Record<string, unknown>)
      }
    />
  );
}
